import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { apiClient, APIError } from '@/utils/api-client';

export interface BankCode {
  id: string;
  country_code: string;
  bank_code: string;
  bic: string;
  bank_name: string;
  created_at: string;
  updated_at: string;
}

interface BankCodeListResponse {
  status: string;
  items: BankCode[];
  total: number;
  page: number;
  page_size: number;
}

interface BankCodeCountriesResponse {
  status: string;
  data: string[];
}

interface BankCodeCreate {
  country_code: string;
  bank_code: string;
  bic: string;
  bank_name: string;
}

interface BankCodeUpdate {
  bic?: string;
  bank_name?: string;
}

// Get paginated bank codes
export function useBankCodes(params?: {
  page?: number;
  page_size?: number;
  search?: string;
  country_code?: string;
}) {
  return useApiQuery<BankCodeListResponse>(
    ['bankCodes', params],
    () => apiClient.get<BankCodeListResponse>('/admin/bank-codes', { params }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}

// Slim bank record returned by the staff bank-name typeahead.
export interface BankCodeOption {
  id: string;
  country_code: string;
  bic: string;
  bank_name: string;
}

interface BankCodeSearchResponse {
  items: BankCodeOption[];
}

// Search the registry for the bank-name dropdown (staff, non-admin endpoint).
// Scoped to a country when one is given so results stay relevant; only runs
// while the dropdown is open to avoid background fetches.
export function useBankCodeSearch(params: {
  search: string;
  country_code?: string;
  enabled?: boolean;
}) {
  return useApiQuery<BankCodeSearchResponse, unknown, BankCodeOption[]>(
    ['bankCodeSearch', params.search, params.country_code],
    () =>
      apiClient.get<BankCodeSearchResponse>('/banking/bank-codes', {
        params: { q: params.search, country_code: params.country_code },
      }),
    {
      enabled: params.enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
      select: (response) => response.items,
    }
  );
}

// Get distinct country codes present in the registry
export function useBankCodeCountries() {
  return useApiQuery<BankCodeCountriesResponse, unknown, string[]>(
    ['bankCodeCountries'],
    () =>
      apiClient.get<BankCodeCountriesResponse>('/admin/bank-codes/countries'),
    {
      staleTime: 60 * 60 * 1000, // 1 hour - these rarely change
      select: (response) => response.data,
    }
  );
}

// Create bank code
export function useCreateBankCode() {
  return useApiMutation<BankCode, APIError, BankCodeCreate>(
    (data) => apiClient.post('/admin/bank-codes', data),
    {
      mutationKey: ['createBankCode'],
      invalidateKeys: [['bankCodes'], ['bankCodeCountries']],
      successMessage: 'Bank code created successfully',
    }
  );
}

// Update bank code
export function useUpdateBankCode() {
  return useApiMutation<
    BankCode,
    APIError,
    { id: string; data: BankCodeUpdate }
  >(({ id, data }) => apiClient.put(`/admin/bank-codes/${id}`, data), {
    mutationKey: ['updateBankCode'],
    invalidateKeys: [['bankCodes']],
    successMessage: 'Bank code updated successfully',
  });
}

// Delete bank code
export function useDeleteBankCode() {
  return useApiMutation<void, APIError, string>(
    (id) => apiClient.delete(`/admin/bank-codes/${id}`),
    {
      mutationKey: ['deleteBankCode'],
      invalidateKeys: [['bankCodes'], ['bankCodeCountries']],
      successMessage: 'Bank code deleted successfully',
    }
  );
}
